
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoCheckmarkCircle } from "react-icons/io5";

const formatNaira = (value) => {
  if (typeof value === "number" && !isNaN(value)) {
    return `₦${value.toLocaleString("en-NG")}`;
  }
  return "₦0";
};

export default function PaymentScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  // Expect payment details via location.state
  const {
    grandTotal = 0,
    deliveryFee = 0,
    subtotalPrice = 0,
    totalItems = 0,
    paymentReference = "",
    accountName = "",
    accountNumber = "",
    bankName = "",
    amountToPay = 0,
    paymentType = ""
  } = location.state || {};

  const [countdown, setCountdown] = useState(1200); // 20 minutes (1200 seconds)
  const [orderPlaced] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    // Start countdown
    if (countdown > 0 && !orderPlaced) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown, orderPlaced]);

  useEffect(() => {
    const reference = paymentReference || "";
    if (!reference) return;
    let isActive = true;
    const intervalId = setInterval(async () => {
      try {
        const buyerToken = localStorage.getItem("userToken");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/verify/${reference}`, {
          headers: buyerToken ? { Authorization: `Bearer ${buyerToken}` } : {},
        });
        const data = await res.json();
        const status = (data?.status || data?.data?.status || "").toLowerCase();
        if (!isActive) return;
        if (status === "paid") {
          clearInterval(intervalId);
          navigate("/orders");
        } else if (status === "failed" || status === "expired") {
          clearInterval(intervalId);
          setPaymentError("Payment failed or expired. Please try again.");
        }
      } catch {
        // Keep polling on transient errors
      }
    }, 2000);

    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [paymentReference, navigate]);


  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="w-full max-w-md mx-auto p-6 bg-white rounded-2xl shadow-lg text-center">
        <h1 className="text-xl font-extrabold text-gray-900 mb-2">Payment Details</h1>
        <div className="mb-4">
          <div className="flex flex-col gap-2 text-left mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-800">Account Name</span>
              <span className="text-sm font-extrabold text-gray-900">{accountName || "-"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-800">Account Number</span>
              <span className="text-sm font-extrabold text-gray-900">{accountNumber || "-"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-800">Bank Name</span>
              <span className="text-sm font-extrabold text-gray-900">{bankName || "-"}</span>
            </div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-gray-800">Total Items ({totalItems})</span>
            <span className="text-sm font-extrabold text-gray-900">{formatNaira(subtotalPrice)}</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-gray-800">Delivery</span>
            <span className="text-sm font-extrabold text-gray-900">{formatNaira(deliveryFee)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 pt-4">
            <span className="text-base font-black text-gray-900">Total Price</span>
            <span className="text-base font-black text-gray-900">{formatNaira(grandTotal)}</span>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-base font-black text-gray-900">Amount to Pay ({paymentType?.toUpperCase()})</span>
            <span className="text-base font-black text-[#19C463]">{formatNaira(amountToPay)}</span>
          </div>
        </div>
        {!orderPlaced ? (
          <div className="mt-6">
            <p className="text-lg font-bold text-gray-800">Waiting for order confirmation...</p>
            <p className="text-sm text-gray-600">You will be redirected once your order is placed.</p>
            <div className="mt-4 text-2xl font-black text-[#19C463]">
              {`${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}`} min
            </div>
            {paymentError && (
              <p className="mt-3 text-sm font-semibold text-red-600">{paymentError}</p>
            )}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center">
            <IoCheckmarkCircle size={70} className="text-[#19C463] mb-2" />
            <p className="text-lg font-extrabold text-gray-900">Order placed! 🎉</p>
            <p className="text-sm font-semibold text-gray-700 leading-relaxed">You will be redirected to checkout.</p>
          </div>
        )}
      </div>
    </div>
  );
}
